/**
 * Single draggable flag shortcut for a pinned country.
 * For Media Devoted | Created By Ciro Autiero
 */

import * as RadixTooltip from '@radix-ui/react-tooltip';

import { countryToFlagEmoji, countryKeyToIso2 } from '@/utils/country-flag-emoji';
import { cn } from '@/lib/utils';

export function DraggableCountryPin({
  c,
  onNavigate,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  isDragging,
}: {
  c: { countryKey: string; countryName?: string };
  onNavigate: (key: string) => void;
  onDragStart: (e: React.DragEvent, key: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent, key: string) => void;
  isDragging: boolean;
}) {
  const emoji = countryToFlagEmoji(c.countryKey);
  const iso = countryKeyToIso2(c.countryKey);

  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>
        <div
          draggable
          onDragStart={(e) => onDragStart(e, c.countryKey)}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDrop={(e) => onDrop(e, c.countryKey)}
          className={cn(
            'flex cursor-grab flex-col items-center justify-center gap-0.5 rounded-md px-1.5 py-1.5',
            'min-w-[36px] select-none transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing',
            'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
            isDragging && 'opacity-50',
          )}
        >
          <button
            type="button"
            onClick={() => onNavigate(c.countryKey)}
            className="flex flex-col items-center gap-0.5 focus:outline-none"
            aria-label={`Open ${c.countryName ?? c.countryKey}`}
          >
            <span className="text-lg">{emoji}</span>
            <span className="text-[10px] font-medium uppercase text-muted-foreground">{iso || '—'}</span>
          </button>
        </div>
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side="top"
          sideOffset={6}
          avoidCollisions
          collisionPadding={12}
          className="z-[300] rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
        >
          {c.countryName ?? c.countryKey} – Open (drag to reorder)
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}
